import { AutoSaveIndicator } from '@/components/common/auto-save-indicator';
import { LoadingBlockFullPage } from '@/components/loading/loading-block-full-page';
import { Button } from '@/components/ui/button';
import { DrawerContainer, DrawerContent, DrawerHeader } from '@/components/ui/drawer';
import { Text } from '@/components/ui/text';
import { useAppConfig } from '@/features/acl/acl-provider';
import { useAnalytics } from '@/features/analytics/hooks/use-analytics';
import { CommentsThread } from '@/features/comments/components/comments-thread';
import { DialogStateManager } from '@/features/dialog/dialog-provider';
import { useUpdateNote } from '@/features/notes/hooks/use-update-note';
import { TraskCreateDialog } from '@/features/tasks/components/task-create-dialog';
import { useLocale } from '@/lib/hooks/use-locale';
import { SearchEntity } from '@/server/types/search-types';
import { api } from '@/utils/api';
import { getEventMetadata, hasOccurrenceOn } from '@/utils/event-utils';
import { PlusIcon } from '@heroicons/react/24/outline';
import { intlFormat, isFuture, isSameDay } from 'date-fns';
import { EventDetailsSchedule } from './event-details-schedule';
import { EventEditDialog } from './event-edit-dialog';
import { EventNoteCreate } from './event-note-create';
import { EventNoteForm } from './event-note-form';
import { EventOptionsDropdown } from './event-options-dropdown';

export type EventDetailsProps = {
    date: string;
    eventId: string;
    className?: string;
    onBack?: () => void;
    onClose?: () => void;
    onUpdated?: () => void;
};

export const EventDetails = ({ date, eventId, className, onBack, onClose, onUpdated }: EventDetailsProps) => {
    const { t, locale } = useLocale('events');
    const sendEvent = useAnalytics();
    const { userId } = useAppConfig();

    // Use the direct getEventById endpoint
    const { data: eventDetails, isLoading } = api.platoUser.query.events.getEventById.useQuery(
        {
            eventId,
        },
        {
            enabled: !!eventId,
        },
    );

    const updateNoteMutation = useUpdateNote();

    const handleEdit = () => {
        if (!eventDetails) return;
        DialogStateManager.show(EventEditDialog, {
            eventId,
            eventDetails,
            onUpdated,
        });
    };

    if (isLoading || !eventDetails) {
        return <LoadingBlockFullPage className="mt-64" />;
    }

    // Determine if the current user is the owner of the event
    const isOwner = eventDetails.owner_id === userId;

    const occurenceDate = new Date(date);
    const isOccurrence = hasOccurrenceOn(eventDetails, date);
    const noteDetails = eventDetails.notes.find((note) => isSameDay(note.occurredAt, occurenceDate));

    return (
        <DrawerContainer className={className}>
            <DrawerHeader
                onBack={onBack}
                onClose={onClose}
                actions={
                    eventDetails &&
                    isOwner && (
                        <>
                            <Button
                                variant="subtle"
                                icon={PlusIcon}
                                onClick={() => {
                                    DialogStateManager.show(TraskCreateDialog, {
                                        links: eventDetails.customer?.customerId
                                            ? [
                                                  {
                                                      modelType: SearchEntity.Customer,
                                                      modelId: eventDetails.customer.customerId,
                                                      title: eventDetails.customer.customerName,
                                                  },
                                              ]
                                            : undefined,
                                    });
                                }}
                            >
                                {t('details__new_task', { ns: 'events' })}
                            </Button>
                            <EventOptionsDropdown
                                date={date}
                                event={eventDetails}
                                onEdit={handleEdit}
                                onDelete={() => {
                                    onUpdated?.();
                                    onClose?.();
                                    sendEvent('event_deleted', getEventMetadata(eventDetails));
                                }}
                            />
                        </>
                    )
                }
                title={t('details__title')}
            >
                <AutoSaveIndicator
                    isUpdating={updateNoteMutation.isLoading}
                    isSuccess={updateNoteMutation.isSuccess}
                    isError={updateNoteMutation.isError}
                />
            </DrawerHeader>
            <DrawerContent>
                <EventDetailsSchedule eventDetails={eventDetails} />

                {isOccurrence && (noteDetails || !isFuture(occurenceDate)) && (
                    <div className="flex flex-col gap-3">
                        <Text color="neutral-950" weight="medium">
                            {t('note_for_date', {
                                date: intlFormat(
                                    occurenceDate,
                                    {
                                        month: 'long',
                                        day: 'numeric',
                                        year: 'numeric',
                                    },
                                    { locale },
                                ),
                            })}
                        </Text>

                        {noteDetails ? (
                            <div className="flex flex-col gap-3">
                                {/* Display the editable note */}
                                <EventNoteForm
                                    note={noteDetails}
                                    onUpdate={(values) => {
                                        updateNoteMutation.mutate({
                                            noteId: noteDetails.noteId,
                                            title: values.title,
                                        });
                                    }}
                                />

                                {/* Show comments thread */}
                                <CommentsThread noteId={noteDetails.noteId} />
                            </div>
                        ) : (
                            <EventNoteCreate event={eventDetails} occurrenceDate={date} />
                        )}
                    </div>
                )}
            </DrawerContent>
        </DrawerContainer>
    );
};
